import decodeHtmlEntitiesRecursive from '@core/utils/helpers/decodeHtmlEntitiesRecursive'

import React from 'react'

import clsx from 'clsx'
import dayjs from 'dayjs'

import './ApplyV2ReviewApplyEducation.scss'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import duration from 'dayjs/plugin/duration'
import { useTranslation } from 'react-i18next'

import type { ApplyV2TempEducation } from '@core/app/types/applyV2Type'
import ApplyV2DataRow from '@core/components/ApplyDialogV2/ApplyV2DataRow'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'

dayjs.extend(duration)
dayjs.extend(customParseFormat)

const baseClass = 'ApplyV2ReviewApplyEducation'

interface ApplyV2ReviewApplyEducationProps {
    className?: string
    isShowExpandButton?: boolean
}

function ApplyV2ReviewApplyEducation({ className = '' }: ApplyV2ReviewApplyEducationProps) {
    const {
        postProfileData,
        postProfileError,
        isPostProfileLoading,
        isPostProfileSuccess,
        isSettingsLoading,
        isSettingsFetching,
    } = useApplyV2Context()
    const { t } = useTranslation()
    const data = postProfileData?.data?.education || []
    const isEmpty = data.length === 0
    const noFields = postProfileData?.data?.noFields ?? []

    return (
        <div
            className={clsx(baseClass, className, {
                [`${baseClass}--isEmpty`]: isEmpty,
            })}
        >
            {noFields.includes('education') ? (
                <div>{t('10308', "I don't have any education")}</div>
            ) : isEmpty ? (
                <div>{t('10701', 'No education added')}</div>
            ) : (
                data.map((education) => (
                    <ApplyV2ReviewApplyEducationItem key={education.uid || education.existingId} {...education} />
                ))
            )}
        </div>
    )
}

function ApplyV2ReviewApplyEducationItem({
    uid,
    existingId,
    schoolName,
    degree,
    location,
    fromDate,
    toDate,
    educationListId,
    description,
    educationType,
}: ApplyV2TempEducation) {
    function formatPeriod(fromDate?: string | null, toDate?: string | null): string {
        if (!fromDate && !toDate) return ''

        const from = fromDate ? dayjs(fromDate) : null
        const to = toDate ? dayjs(toDate) : dayjs()

        const range = [from ? from.format('MMM YYYY') : '', toDate ? to.format('MMM YYYY') : 'Present']
            .filter(Boolean)
            .join(' – ')

        if (!from) return range

        const years = to.diff(from, 'year')
        const months = to.diff(from.add(years, 'year'), 'month')

        const parts = []
        if (years > 0) parts.push(`${years} year${years > 1 ? 's' : ''}`)
        if (months > 0) parts.push(`${months} month${months > 1 ? 's' : ''}`)

        const durationStr = parts.length ? ` (${parts.join(' ')})` : ''

        return range + durationStr
    }

    return (
        <div className={`${baseClass}__item`}>
            <div className={`${baseClass}__item--name`}>{decodeHtmlEntitiesRecursive(schoolName)}</div>
            {(degree || educationType) && (
                <div className={`${baseClass}__item--position`}>
                    {degree && (
                        <span className={`${baseClass}__item--position-title`}>
                            {decodeHtmlEntitiesRecursive(degree)}
                        </span>
                    )}
                    {degree && educationType && <span className={`${baseClass}__item--position-dot`}></span>}
                    {educationType && (
                        <span className={`${baseClass}__item--position-role`}>
                            {decodeHtmlEntitiesRecursive(educationType)}
                        </span>
                    )}
                </div>
            )}
            {(fromDate || toDate) && (
                <ApplyV2DataRow fieldType={'date'} className={`${baseClass}__item--mobilePhone`}>
                    {formatPeriod(fromDate, toDate)}
                </ApplyV2DataRow>
            )}
            {location && (
                <ApplyV2DataRow fieldType={'location'} className={`${baseClass}__item--email`}>
                    {decodeHtmlEntitiesRecursive(location)}
                </ApplyV2DataRow>
            )}
            {description && (
                <div className={`${baseClass}__item--description`}>{decodeHtmlEntitiesRecursive(description)}</div>
            )}
        </div>
    )
}

export default ApplyV2ReviewApplyEducation
